import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import LocationSearchListContent from '../../components/search/LocationSearchListContent';
import SearchForm from '../../components/search/SearchForm/SearchForm';
import SearchMapContainer from '../../components/search/SearchMapContainer';
import SearchMapToggle from '../../components/search/SearchMapToggle';
import {NavigationType, useLocation, useNavigate, useNavigationType} from 'react-router';
import {getLocationSuppliers} from '../../components/location/requests/location-requests';
import {getErrorMessage} from '../../util/NetworkErrorUtil';
import moment from 'moment';
import LocationSearchNoContent from '../../components/search/location-search-no-content/LocationSearchNoContent';
import '../search.css'
import {DateFormats} from "../../components/constants/securspace-constants";
import {useSnackbar} from "../../context/snackbar-context";
import {AppContext} from '../../context/app-context';
import type {Supplier} from "../../types/Supplier";
import {hasAdminAccess} from "../../util/AccountUtils";
import {useFeatureFlags} from "../../hooks/routing/useFeatureFlags";
import {LeftContainer, MainGridContainer, SearchResultsContainer} from "./styles";
import {useSearchSupplierSortBy} from "../../hooks/search/useSearchSupplierSortBy";
import {useSortBySearchParam} from "../../hooks/supplier/useSortBySearchParam";
import {useSupplierFilters} from "../../hooks/supplier/useSupplierFilters";
import {DEFAULT_INIT_LAT, DEFAULT_INIT_LNG, useDefaultLocation} from "../../hooks/maps/useDefaultLocation";
import {useGeocoder} from "../../hooks/maps/useGeocoder";
import {useLocationSearchParams} from "../../hooks/search/useLocationSearchParams";
import {usePrerenderReady} from "../../hooks/usePrerenderReady";
import {isSearchListSettled} from "../../util/SearchUtils";
import {useGeocodedPlaceByParams} from "../../hooks/search/useGeocodedPlaceByParams";
import {getQueryParamsFromPacItem, getQueryParamsFromPlace, parseLatLngParams} from "../../util/AddressUtils";
import {useGeocodedPlaceByLocalStorageLatLng} from "../../hooks/search/useGeocodedPlaceByLocalStorageLatLng";
import {saveGeocodedPlaceToLocalStorage} from "../../util/PlaceStorageUtil";
import {useGeocodedPlaceByBrowserLatLng} from "../../hooks/search/useGeocodedPlaceByBrowserLatLng";
import {useGeocodedPlaceByIpLatLng} from "../../hooks/search/useGeocodedPlaceByIpLatLng";
import {useRedirectSearchByLocationParams} from "../../hooks/search/useRedirectSearchByLocationParams";
import SearchSEO from "../../components/search/SearchSEO";
import SearchResultsHeading from "../../components/search/SearchResultsHeading";
import {stateAbbr} from "../../components/location/profile/LocationProfileSEO";
import Footer from "../../components/footer/Footer";
import MarketSummary from "../../components/search/MarketSummary";
import {Box} from "@mui/material";
import {getCachedLocationSuppliers, shouldUseCache} from "./cache/LocationCache";
import {useDistanceBetween} from "../../hooks/maps/useDistanceBetween";
import useSupplierMapBoundaryFilter from "../../hooks/supplier/useSupplierMapBoundaryFilter";
import useSearchRadiusFilter from "../../hooks/supplier/useSearchRadiusFilter";
import {PlacePrediction} from "../../components/placesAutocomplete/interfaces";
import {useSearchMapToggle} from "../../hooks/search/useSearchMapToggle";
import {getLatLng, sortByMilesFrom} from "../../util/SupplierUtils";
import {findMarketByCity, slugify} from "../../seo/markets.mjs";
import {generateLocationProfileLink} from "../../util/LinkUtils";

const DEFAULT_DAYS_AHEAD = 30;
const DEFAULT_NUM_SPACES = 1;
const DEFAULT_ERROR_MESSAGE = "Failed to fetch locations";

type GeocoderResult = google.maps.GeocoderResult;

const DEFAULT_RADIUS_MILES: Readonly<number> = 50;

// Debounce for writing the dragged map center to the URL — a pan writes once
// after it settles. No geocoding is involved, so this only governs (free) URL
// updates; separate from the shorter bounds-filter debounce inside the map.
const MAP_DRAG_URL_DEBOUNCE_MS = 400;

// The search box represents a searched *area*, so it must only ever display a
// locality (city, state, country). We intentionally omit any street-level
// address parts here — otherwise a precise geocode result (e.g. when city/state
// are missing from the URL and the coordinates resolve to a street address)
// would surface a street address in the search box, which should never happen.
// US places read "Los Angeles, CA", like the results heading under the box, so the
// city still fits beside the List / Map switch on a phone; elsewhere the country stays.
const formatPlace = (place: GeocoderResult | null | undefined): string => {
  if (!place) return '';
  const {city, state, country} = getQueryParamsFromPlace(place, 'long_name', false) || {};
  return [city, stateAbbr(state), country === 'United States' ? '' : country].filter(Boolean).join(', ');
};

// Minimal *synthetic* GeocoderResult carrying only coordinates — used when a
// lat/lng URL reverse-geocodes to no locality (e.g. rural areas), so we still
// center and fetch suppliers at the user's explicit coordinates rather than
// redirecting away. NOTE: it has no real Google methods/fields beyond
// geometry.location and address_components — consumers must only read those.
const makePlaceFromCoords = (lat: number, lng: number): GeocoderResult => ({
  geometry: { location: new google.maps.LatLng(lat, lng) },
  address_components: [],
  formatted_address: '',
  place_id: '',
  types: [],
} as unknown as GeocoderResult);

const Search = () => {
  const {snackbarShowMessage} = useSnackbar();
  const {loading: geocoderLoading, geocoder} = useGeocoder();
  const {user} = useContext(AppContext);
  const [unfilteredLocations, setUnfilteredLocations] = useState<Array<Supplier>>([]);

  const navigate = useNavigate();
  const [currentLocationParams, setCurrentLocationParams, cityFromPath] = useLocationSearchParams();
  // Latest URL params, read inside effects/timeouts without re-subscribing them
  // (so the drag-write timer and the place-sync effect don't loop on their own writes).
  const currentLocationParamsRef = useRef(currentLocationParams);
  currentLocationParamsRef.current = currentLocationParams;
  const cityFromPathRef = useRef(cityFromPath);
  cityFromPathRef.current = cityFromPath;

  // Form inputs / filters
  const [instantApprovalOnly, setInstantApproval] = useState(false);
  const [onlyLiveLocations, setOnlyLiveLocations] = useState(true);
  const [showRequestLocationForm, setShowRequestLocationForm] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [selectedSort, setSelectedSort] = useSortBySearchParam('distance');

  const filterState = useMemo(() => ({
    selectedEquipmentType: selectedEquipment,
    setSelectedEquipmentType: setSelectedEquipment,
    selectedLocationFeatures: selectedFeatures,
    setSelectedLocationFeatures: setSelectedFeatures,
    selectedSort,
    setSelectedSort,
  }), [selectedEquipment, setSelectedEquipment, selectedFeatures, setSelectedFeatures, selectedSort, setSelectedSort]);

  // UI state
  const {isMapActive, toggleController} = useSearchMapToggle();
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  // True once a supplier fetch has completed at least once — gates the
  // viewport-driven form visibility so the request form can't flash before
  // suppliers have loaded.
  const [hasFetchedSuppliers, setHasFetchedSuppliers] = useState(false);
  // A failed fetch leaves no yards, which must not read as "no yards here" (SearchSEO).
  const [supplierFetchFailed, setSupplierFetchFailed] = useState(false);
  const [listHover, setListHover] = useState(null);

  // Search location state
  // Map's commanded center. Seeded from the URL viewport coords (so a reloaded
  // or shared "dragged" URL opens where the map was left), falling back to the
  // default. Only re-commanded on a new search / place resolution — never on
  // drag (drag writes the URL directly; see the drag-write effect below).
  const [mapPosition, setMapPosition] = useState<google.maps.LatLngLiteral>(
    () => parseLatLngParams(currentLocationParams) ?? {
      lat: DEFAULT_INIT_LAT,
      lng: DEFAULT_INIT_LNG
    }
  );
  const [currentPlace, setCurrentPlace] = useState<GeocoderResult>();
  // Drag (coordinate) mode: once the user drags the map, the search becomes
  // coordinate-based — the search box clears, the URL keeps only lat/lng
  // (city/state/place_id dropped), and we do NOT geocode. The request form's
  // city/state is filled by the user via autocomplete instead. Reset on a new
  // search. Ephemeral (not derived from the URL), so a reloaded coordinate URL
  // resolves a label normally.
  const [isDragMode, setIsDragMode] = useState(false);
  // True once the user has genuinely dragged the map this session — distinguishes
  // a real drag from a programmatic recenter (new search / initial load).
  const userHasDraggedRef = useRef(false);
  // The map center the user last dragged away from; a new search commands a new one.
  const [draggedFrom, setDraggedFrom] = useState<google.maps.LatLngLiteral>();
  const [atSearchZoom, setAtSearchZoom] = useState(false);
  const handleUserDragStart = useCallback(() => {
    userHasDraggedRef.current = true;
    setDraggedFrom(mapPosition);
  }, [mapPosition]);

  // Effective coordinate mode = dragged this session AND the URL is coords-only.
  // If the URL regains a city/state (browser back/forward to an earlier ?city=
  // entry, or a new search), we are no longer coordinate-driven even if the drag
  // flag still lingers — so the restored location resolves and shows in the box
  // instead of staying blank. Derived (not the raw flag) to avoid a reset race.
  const inCoordinateMode = isDragMode && !(currentLocationParams?.city && currentLocationParams?.state);

  // Map bounds state (managed by SearchMapContainer, exposed via callbacks)
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | undefined>();
  const [mapBoundsInitialized, setMapBoundsInitialized] = useState(false);
  // Crawler snapshot waits for the list to show real results: the fetch, and the map's
  // bounds the list filters by. Otherwise a fetch that beats the bounds snapshots the
  // skeleton, without the yards or a market's summary (#3142 review).
  const listSettled = isSearchListSettled({hasFetchedSuppliers, loadingSuppliers, mapBoundsInitialized, yardCount: unfilteredLocations.length});
  usePrerenderReady(listSettled);


  const {optimizedLocationSearch, searchMapV2, cachedSupplierSearch, supplierCapacitySchedule} = useFeatureFlags();

  // Derived display value for the search input; only recomputes when currentPlace changes (not on every keystroke), preventing parent re-renders during typing.
  const formattedSearchValue = useMemo(() => {
    return formatPlace(currentPlace);
  }, [currentPlace]);

  // these are the different hooks which handle logic to get an address from somewhere to display
  // they are set up to cascade in the order that they fail, then the useRedirectSearch hook will handle redirecting to the new location
  const {geocodedAddressFromParams, geocodedAddressFromParamsLoading, geocodingAddressFromParamsFailed} = useGeocodedPlaceByParams({
    currentPlace,
    currentLocationParams,
    skip: inCoordinateMode
  });

  const {
    geocodedPlaceByLocalStorageLatLng,
    failedToGetByLocalStorage
  } = useGeocodedPlaceByLocalStorageLatLng({hasCurrentPlace: !!currentPlace, shouldTryLocalStorage: geocodingAddressFromParamsFailed});

  const {geocodedPlaceByBrowserLatLng, failedToGetByBrowserLatLng} = useGeocodedPlaceByBrowserLatLng({
    currentPlace,
    shouldTryBrowserLatLng: failedToGetByLocalStorage,
    currentLocationParams
  });
  const {geocodedPlaceByIpLatLng, failedToGetByIp} = useGeocodedPlaceByIpLatLng({
    currentPlace,
    shouldTryGeocodeByIp: failedToGetByBrowserLatLng
  })
  const {defaultLocation} = useDefaultLocation();

  // Back/Forward restores an earlier address: resolve its place again, since the
  // bootstrap below only runs while currentPlace is unset (securspace-ui#3053).
  // Also when a link elsewhere in the app opens another place on this same page (the
  // assistant's map link, the nav's Search item, securspace-ui#3054). The page's own
  // writes replace, except the search box, whose push carries a place_id.
  // Only when the place changed: Back over a sort change keeps the page as it is.
  const routeLocation = useLocation();
  const {key: historyKey} = routeLocation;
  const navigationType = useNavigationType();
  const placeKey = JSON.stringify(currentLocationParams ?? null);
  const lastPlaceKeyRef = useRef(placeKey);
  useEffect(() => {
    const placeChanged = lastPlaceKeyRef.current !== placeKey;
    lastPlaceKeyRef.current = placeKey;
    const outsideLink = navigationType === NavigationType.Push && !currentLocationParams?.place_id;
    if (!placeChanged || !(navigationType === NavigationType.Pop || outsideLink)) return;
    userHasDraggedRef.current = false;
    setIsDragMode(false);
    setCurrentPlace(undefined);
  }, [historyKey, navigationType, placeKey, currentLocationParams?.place_id]);

  // Bootstrap currentPlace from the geocoded URL params (initial load / URL-driven nav).
  // Only runs while currentPlace is unset so it never clobbers a later place transition.
  useEffect(() => {
    if (!currentPlace && geocodedAddressFromParams) {
      // URL address is valid and geocoded properly, set currentPlace state to reflect it
      setCurrentPlace({...geocodedAddressFromParams});
      // Prefer the URL viewport coords (e.g. a reloaded dragged position) over the
      // geocoded place center, so reload/share opens where the map was left.
      setMapPosition(parseLatLngParams(currentLocationParamsRef.current) ?? {
        lat: geocodedAddressFromParams.geometry.location.lat(),
        lng: geocodedAddressFromParams.geometry.location.lng()
      });
    }
  }, [currentPlace, geocodedAddressFromParams]);

  // Fallback for a valid lat/lng URL that reverse-geocodes to no locality (rural
  // areas): synthesize a coords-only place so the map centers + fetches at those
  // coordinates instead of redirecting away. Skipped in drag mode (currentPlace
  // stays frozen) and once a real place has resolved.
  useEffect(() => {
    if (currentPlace || isDragMode) return;
    if (geocodedAddressFromParams || geocodedAddressFromParamsLoading) return;
    const cur = currentLocationParamsRef.current;
    // A city/state URL (e.g. landing-page "popular cities" links) is resolved by
    // the forward-geocode cascade — never synthesize over it. That cascade's
    // loading flag can read false on the very first render, so this guard (not
    // the loading flag) is what stops the synthetic coords-only place from
    // clobbering the real place and stripping city/state from the URL.
    if (cur?.city && cur?.state) return;
    const coords = parseLatLngParams(cur);
    if (!coords) return;
    setCurrentPlace(makePlaceFromCoords(coords.lat, coords.lng));
  }, [currentPlace, isDragMode, geocodedAddressFromParams, geocodedAddressFromParamsLoading]);

  // Sync the map position + URL params from the resolved place.
  // IMPORTANT: this depends ONLY on currentPlace. If it also depended on
  // geocodedAddressFromParams (which briefly becomes undefined while a newly
  // submitted location is being geocoded), this effect would re-run with a STALE
  // currentPlace and write the old location's params back to the URL — reverting
  // the search and cancelling the in-flight place_id geocode. That is exactly why
  // searching a location with no results appeared to do nothing.
  useEffect(() => {
    if (currentPlace) {
      // Prefer the URL viewport coords (dragged position) over the place center,
      // so resolving the anchor place for the search box doesn't yank the map
      // back to the city center on reload.
      const urlViewport = parseLatLngParams(currentLocationParamsRef.current);
      setMapPosition(urlViewport ?? {
        lat: currentPlace.geometry.location.lat(),
        lng: currentPlace.geometry.location.lng(),
      });
      // Ensure URL params always reflect the current place, even if no suppliers exist.
      // Exclude street-level details (street_number, street_name) — the search page
      // represents an area search, so the URL should not carry a fabricated street
      // address from reverse-geocoding (locality fields + lat/lng remain).
      const params = getQueryParamsFromPlace(currentPlace, 'short_name', false);
      if (params) {
        // The viewport coords are owned by the map (written on drag). When the URL
        // already carries them, keep them so the anchor place can't overwrite where
        // the user panned to; otherwise seed lat/lng from the place center.
        if (urlViewport) {
          params.lat = currentLocationParamsRef.current?.lat;
          params.lng = currentLocationParamsRef.current?.lng;
        }
        // This fills in the address of the place already shown, so it replaces the
        // history entry rather than adding a Back-button step (securspace-ui#3053).
        if (cityFromPathRef.current && params.city) {
          // A /yards city path already names the place, a market by its market-list
          // name, so the geocoder never rewrites it: only the map centre is added.
          // A place with no city (e.g. a Brooklyn street address) drops the path: it
          // couldn't be geocoded back from it, so it reloads from its coordinates.
          const {city, state, country} = currentLocationParamsRef.current ?? {};
          setCurrentLocationParams({city, state, country, lat: params.lat, lng: params.lng}, {replace: true});
        } else {
          setCurrentLocationParams(params, {replace: true});
        }
      }
    }
  }, [currentPlace, setCurrentLocationParams]);

  // Save currentPlace to local storage whenever it changes
  useEffect(() => {
    // Only cache real, resolvable places — never a synthesized coords-only place
    // (no locality), so the local-storage fallback can't later resurrect it.
    if (currentPlace && getQueryParamsFromPlace(currentPlace)?.city) {
      saveGeocodedPlaceToLocalStorage(currentPlace);
    }
  }, [currentPlace]);


  // redirect if necessary
  useRedirectSearchByLocationParams({
    defaultLocation,
    failedToGetByBrowserLatLng,
    failedToGetByIp,
    failedToGetByLocalStorage,
    geocodedPlaceByBrowserLatLng,
    geocodedPlaceByIpLatLng,
    geocodedPlaceByLocalStorageLatLng,
    geocodingAddressFromParamsFailed: geocodingAddressFromParamsFailed,
  });

  // location filtering by map boundary (controlled)
  // Don't filter until map bounds are initialized to prevent flashing of incorrect results
  const {filteredSuppliers: mapBoundaryFilteredSuppliers} = useSupplierMapBoundaryFilter({
    unfilteredSuppliers: mapBoundsInitialized ? unfilteredLocations : [],
    bounds: mapBounds
  });

  // --- Drag → update the URL coordinates (coordinate mode) ---
  // On a genuine user drag (debounced once the map settles), write only lat/lng
  // and drop city/state/place_id. No geocoding — the search box clears and the
  // request form's city/state is user-entered via autocomplete. The supplier
  // fetch is untouched (the existing bounds filter drives the list).
  const dragUrlWriteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // Only react to a genuine user drag — a programmatic recenter (new search /
    // initial load) must not enter coordinate mode.
    if (!mapBoundsInitialized || !mapBounds || !userHasDraggedRef.current) return;
    const center = mapBounds.getCenter();
    if (!center) return;
    const lat = String(center.lat());
    const lng = String(center.lng());

    if (dragUrlWriteTimeoutRef.current) clearTimeout(dragUrlWriteTimeoutRef.current);
    dragUrlWriteTimeoutRef.current = setTimeout(() => {
      // Bail if a new search happened after this timer was scheduled (it resets
      // the flag) — otherwise a stale drag-write would clobber the search.
      if (!userHasDraggedRef.current) return;
      // Consume the armed drag: only a fresh `dragstart` re-arms it, so later
      // programmatic recenters (browser back/forward to a ?city= URL, a new
      // search) can't re-enter coordinate mode and drop city/state. The
      // "dragged this session" state lives in isDragMode, not this ref.
      userHasDraggedRef.current = false;
      const cur = currentLocationParamsRef.current;
      if (cur?.lat === lat && cur?.lng === lng) return; // no real movement
      setIsDragMode(true);
      setCurrentLocationParams({lat, lng}, {replace: true});
    }, MAP_DRAG_URL_DEBOUNCE_MS);

    return () => {
      if (dragUrlWriteTimeoutRef.current) clearTimeout(dragUrlWriteTimeoutRef.current);
    };
  }, [mapBounds, mapBoundsInitialized, setCurrentLocationParams]);

  // --- Request-a-space form follows the viewport, not just the initial fetch ---
  // Once bounds are known, show the form whenever the current map view has no
  // suppliers (geographic boundary filter — deliberately not the attribute
  // filters), so it hides as the user drags into an area with spaces and returns
  // when they drag back to an empty one.
  useEffect(() => {
    if (!mapBoundsInitialized || loadingSuppliers || !hasFetchedSuppliers) return;
    setShowRequestLocationForm(mapBoundaryFilteredSuppliers.length === 0);
  }, [mapBoundaryFilteredSuppliers, mapBoundsInitialized, loadingSuppliers, hasFetchedSuppliers]);

  // location filtering
  const {filteredLocations} = useSupplierFilters({
    unfilteredLocations: mapBoundaryFilteredSuppliers,
    instantApprovalOnly,
    user,
    onlyLiveLocations,
    selectedFeatures,
    selectedEquipment
  });

  // If using cached suppliers, pass the unfiltered suppliers to add the distance field
  // Otherwise this hook's logic will not run and will not edit unfilteredLocations because the distance field already exists
  useDistanceBetween({
    coordinates: cachedSupplierSearch ? unfilteredLocations : [],
    setCoordinates: cachedSupplierSearch ? setUnfilteredLocations : () => {
    },
    center: cachedSupplierSearch ? mapPosition : null,
    getLatLng
  });

  // location sorting
  const {sortedSuppliers: sortedBySelection} = useSearchSupplierSortBy({searchSortBy: selectedSort, unsortedSuppliers: filteredLocations});
  // After a drag the address no longer names a place (coordinate mode): "Distance" orders by
  // the middle of the map, and the list shows no miles, which would be from the place the user
  // left (securspace-ui#3095).
  const sortedSuppliers = useMemo(() => {
    const middle = mapBounds?.getCenter()?.toJSON();
    return inCoordinateMode && selectedSort === 'distance' && middle ? sortByMilesFrom(sortedBySelection, middle) : sortedBySelection;
  }, [sortedBySelection, inCoordinateMode, selectedSort, mapBounds]);

  const displaySuppliers = useMemo(() => {
    if (!supplierCapacitySchedule) return sortedSuppliers;

    const available: Supplier[] = [];
    const atCapacity: Supplier[] = [];
    sortedSuppliers.forEach((supplier) => {
      if (supplier.hasAvailableCapacityToday === false) {
        atCapacity.push(supplier);
      } else {
        available.push(supplier);
      }
    });
    return atCapacity.length ? [...available, ...atCapacity] : sortedSuppliers;
  }, [sortedSuppliers, supplierCapacitySchedule]);

  // On the view a search opens at, the list and its ItemList name the yards within 50 miles
  // of the place; the map still shows every yard in view (securspace-ui#3088).
  const radiusView = {bounds: mapBounds, center: mapPosition, dragged: draggedFrom === mapPosition, atSearchZoom};
  const listedSuppliers = useSearchRadiusFilter({suppliers: displaySuppliers, ...radiusView});
  const seoSuppliers = useSearchRadiusFilter({suppliers: sortedSuppliers, ...radiusView});

  useEffect(() => {
    let cancelled = false;

    const getDateRange = (numDaysAhead: number) => {
      const today = moment();
      const formattedStartDate = today.format(DateFormats.DAY);

      const futureDate = today.add(numDaysAhead, 'day');
      const formattedEndDate = futureDate.format(DateFormats.DAY);

      return [formattedStartDate, formattedEndDate];
    };

    const fetchSuppliers = async (location: {
      lat: number,
      lng: number
    }, startDate: string, endDate: string, numberOfSpaces: number) => {
      setLoadingSuppliers(true);
      setSupplierFetchFailed(false);
      const shouldFetchFromCache = cachedSupplierSearch && shouldUseCache(window.location.hostname);
      const fetchSuppliersFromApi = async (): Promise<Array<Supplier>> => {
        const response = await getLocationSuppliers(
          optimizedLocationSearch,
          location.lat.toString(),
          location.lng.toString(),
          startDate,
          endDate,
          numberOfSpaces
        );

        return response.body;
      };

      try {
        let suppliers: Array<Supplier>;

        if (shouldFetchFromCache) {
          try {
            // Every search re-requests the file so capacity changed elsewhere shows without a reload (#3345).
            // The browser revalidates with the ETag, so an unchanged file is a ~45 ms 304 with no body.
            const response = await getCachedLocationSuppliers(user, window.location.hostname, {forceRefresh: true});
            suppliers = response.body.locations.map((loc: any) => ({
              ...loc,
              visible: loc.active,
              locationId: loc.id,
              addressLatitude: loc.lat,
              addressLongitude: loc.lon,
              hasInstantApproval: loc.hasInstantApproval ?? false,
              effectiveCapacityToday: loc.effectiveCapacityToday ?? null,
              // Null capacity means unlimited/not schedule-tracked, so it's always treated as available.
              hasAvailableCapacityToday: loc.hasAvailableCapacityToday ?? true,
              nextAvailableDate: loc.nextAvailableDate ?? null,
            }));
          } catch (cacheError: unknown) {
            if (cancelled) return;

            console.error('[Search] Cached supplier fetch failed; falling back to API.', {
              error: cacheError,
              hostname: window.location.hostname,
              cachedSupplierSearch,
              optimizedLocationSearch,
            });

            suppliers = await fetchSuppliersFromApi();
          }
        } else {
          suppliers = await fetchSuppliersFromApi();
        }

        if (cancelled) return;

        const noSuppliers = !suppliers.length;
        setShowRequestLocationForm(noSuppliers);
        setUnfilteredLocations(suppliers);

      } catch (e: any) {
        if (cancelled) return;

        setSupplierFetchFailed(true);
        console.error('[Search] Supplier fetch failed.', {
          error: e,
          hostname: window.location.hostname,
          shouldFetchFromCache,
          cachedSupplierSearch,
          optimizedLocationSearch,
        });

        snackbarShowMessage(`${getErrorMessage(e, DEFAULT_ERROR_MESSAGE)}`, 'error', 15000);
      } finally {
        if (!cancelled) {
          setLoadingSuppliers(false);
          setHasFetchedSuppliers(true);
        }
      }
    }

    if (currentPlace) {
      const [startDate, endDate] = getDateRange(DEFAULT_DAYS_AHEAD);
      void fetchSuppliers({
        lat: currentPlace.geometry.location.lat(),
        lng: currentPlace.geometry.location.lng()
      }, startDate, endDate, DEFAULT_NUM_SPACES);
    }

    return () => {
      cancelled = true;
    };
  }, [currentPlace, optimizedLocationSearch, cachedSupplierSearch, snackbarShowMessage, user]);

  // if cached, unfiltered locations exist, and at least one location is within the default radius, hide the form
  useEffect(() => {
    // Pre-bounds heuristic only: once map bounds are known, the viewport effect
    // above owns form visibility (so dragging hides/shows the form correctly).
    if (mapBoundsInitialized) return;
    if (cachedSupplierSearch && shouldUseCache(window.location.hostname) && unfilteredLocations.length) {
      setShowRequestLocationForm(!unfilteredLocations.some(loc => Number(loc.distance) <= DEFAULT_RADIUS_MILES));
    }
  }, [unfilteredLocations, filteredLocations, cachedSupplierSearch, mapBoundsInitialized]);

  const handleSearchSubmit = useCallback((pacItem: PlacePrediction | undefined) => {
    if (!pacItem?.place_id || geocoderLoading) return;

    const params = getQueryParamsFromPacItem(pacItem)

    // A new explicit search exits coordinate mode and re-anchors on the searched city.
    userHasDraggedRef.current = false;
    setIsDragMode(false);
    // Geocoder provides the place, so that is why we set currentPlace from here.
    setCurrentLocationParams(params);
  }, [geocoderLoading, setCurrentLocationParams])

  useEffect(() => {
    if (currentLocationParams?.place_id && !geocoderLoading && geocoder.geocode) {
      let isActive = true;
      const place_id = currentLocationParams.place_id

      geocoder.geocode({placeId: place_id}, (places: GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        // Guard against stale callback after unmount or dependency change
        if (!isActive) return;

        // Check status first for better error logging
        if (status !== google.maps.GeocoderStatus.OK) {
          console.error('Geocoding failed for place_id:', place_id, 'Status:', status);
          return;
        }

        // Now validate places when status is OK
        if (!places || places.length === 0) {
          console.error('No place results from geocode for place id:', place_id, '(status was OK)');
          return;
        }

        // Always take the first result from geocoding since we are geocoding a specific place_id which should only return one result
        const locationLatLng = places[0].geometry.location || null;
        if (!locationLatLng) {
          navigate('/yards');
        } else {
          setCurrentPlace(places[0]);
          setMapPosition({lat: locationLatLng.lat(), lng: locationLatLng.lng()});
        }
      });

      return () => {
        isActive = false;
      };
    }

  }, [currentLocationParams, geocoder, navigate, geocoderLoading]);


  const handleSelectLocation = useCallback((location: any) => {
    // Absolute: a relative link would resolve under /yards/<cc>/<st>/ and redirect to /yards.
    window.open(generateLocationProfileLink(location.locationId, location.hasInstantApproval && instantApprovalOnly), "_blank");
  }, [instantApprovalOnly]);


  const showAdminOnlyOptions = hasAdminAccess(user);

  // Initial city/state for the request form. Empty in coordinate (drag) mode so
  // the user fills it via the form's city autocomplete; otherwise the searched
  // locality pre-fills it.
  const resolvedPlaceParams = useMemo(() => getQueryParamsFromPlace(currentPlace), [currentPlace]);

  // The path spells a non-market city as a slug ("winston-salem"); once the place
  // resolves to the same slug, name it the geocoder's way (Winston-Salem, McAllen).
  // Markets keep their market-list name.
  const displayLocationParams = useMemo(() => {
    const city = currentLocationParams?.city;
    const resolvedCity = resolvedPlaceParams?.city;
    if (!cityFromPath || !city || !resolvedCity || resolvedCity === city || slugify(resolvedCity) !== slugify(city)
      || findMarketByCity(city, currentLocationParams?.state, currentLocationParams?.country)) {
      return currentLocationParams;
    }
    return {...currentLocationParams, city: resolvedCity};
  }, [currentLocationParams, resolvedPlaceParams, cityFromPath]);
  // The market this page is for: its own list and summary, not after a drag (securspace-ui#3056).
  const pageMarket = useMemo(() => (inCoordinateMode ? undefined
    : findMarketByCity(displayLocationParams?.city, displayLocationParams?.state, displayLocationParams?.country)),
  [inCoordinateMode, displayLocationParams]);
  const requestFormCity = inCoordinateMode ? '' : (resolvedPlaceParams?.city || currentLocationParams?.city || '');
  const requestFormState = inCoordinateMode ? '' : (resolvedPlaceParams?.state || currentLocationParams?.state || '');

  // The search box clears in coordinate (drag) mode; otherwise it shows the
  // searched place.
  const searchBoxValue = inCoordinateMode ? '' : formattedSearchValue;

  return (
    <>
      <SearchSEO
        locationParams={displayLocationParams}
        locations={seoSuppliers}
        fetchedYards={hasFetchedSuppliers && !loadingSuppliers && !supplierFetchFailed ? unfilteredLocations : undefined}
      />
      <MainGridContainer container>
        <SearchResultsContainer container item direction="row" isMapActive={isMapActive}>
          <LeftContainer item isMapActive={isMapActive}>
            <SearchForm
              onSearchSubmit={handleSearchSubmit}
              onInstantApprovalChange={setInstantApproval}
              onLiveLocationsChange={setOnlyLiveLocations}
              searchValue={searchBoxValue}
              instantApprovalOnly={instantApprovalOnly}
              onlyLiveLocations={onlyLiveLocations}
              showAdminOnlyOptions={showAdminOnlyOptions}
              filterState={filterState}
              // Not on the small-screen map: the form follows the yards in the map's bounds, and the
              // filter row's height is the map's, so hiding it could pull an edge yard in and out of view.
              hideFilterControls={showRequestLocationForm && !isMapActive}
              isMapActive={isMapActive}
              onMapToggle={toggleController}
            />
            {
              !showRequestLocationForm &&
                <SearchResultsHeading
                  city={displayLocationParams?.city}
                  state={displayLocationParams?.state}
                  count={hasFetchedSuppliers && !loadingSuppliers ? listedSuppliers.length : undefined}
                  sort={selectedSort}
                  onSortChange={setSelectedSort}
                />
            }
            {
              showRequestLocationForm ?
                <LocationSearchNoContent city={requestFormCity} state={requestFormState} editable={!(requestFormCity && requestFormState)} onCitySearch={handleSearchSubmit}/>
                :
                <LocationSearchListContent
                  locations={listedSuppliers}
                  loading={loadingSuppliers || (!mapBoundsInitialized && unfilteredLocations.length > 0)}
                  instantApproval={instantApprovalOnly}
                  onlyLiveLocations={onlyLiveLocations}
                  listHover={listHover}
                  setListHover={setListHover}
                  showDistance={!inCoordinateMode}
                />
            }
            {/* A market's own text under its list (securspace-ui#3056), from the yards just listed. */}
            {pageMarket && !showRequestLocationForm && hasFetchedSuppliers && !loadingSuppliers && listedSuppliers.length > 0 &&
              <MarketSummary market={pageMarket} yards={listedSuppliers}/>}
            {/* At the end of the list, not below the page: a footer under the results would push
                the map up under the nav, and the yards there stayed listed (securspace-ui#3095).
                Added once the list has loaded, so it doesn't jump down as the results arrive;
                the crawler snapshot waits for the same moment (usePrerenderReady above). */}
            {hasFetchedSuppliers && !loadingSuppliers && <Box sx={{mt: 'auto'}}><Footer location={routeLocation}/></Box>}
          </LeftContainer>

          <SearchMapToggle isMapActive={isMapActive}>
            <SearchMapContainer
              locations={displaySuppliers}
              listHover={listHover}
              onLocationSelect={handleSelectLocation}
              mapCenter={mapPosition}
              useMapV2={searchMapV2}
              onBoundsChanged={setMapBounds}
              onBoundsInitialized={setMapBoundsInitialized}
              onUserDragStart={handleUserDragStart}
              onAtSearchZoomChange={setAtSearchZoom}
            />
          </SearchMapToggle>
        </SearchResultsContainer>
      </MainGridContainer>
    </>
  );
};

export default Search;
